import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Test comment to check if editing works
interface InvoiceItem {
  id?: string;
  item_id?: string;
  description: string;
  qty: number;
  rate: number;
  amount: number;
  is_taxable?: boolean;
}

interface BusinessSettings {
  tax_rate: number;
  late_fee_mode: 'flat' | 'percent' | 'off';
  late_fee_amount: number;
  late_fee_rate?: number;
  currency: string;
}

interface InvoiceTotals {
  subtotal: number;
  taxAmount: number;
  total: number;
  lateFee: number;
  finalTotal: number;
  currency: string;
}

interface InvoiceCalculatorOptions {
  customerTaxExempt?: boolean;
  disableLateFees?: boolean;
}

export const useInvoiceCalculator = (
  items: InvoiceItem[],
  dueDate?: string,
  businessSettings?: BusinessSettings,
  options?: InvoiceCalculatorOptions
) => {
  const { currentBusiness } = useAuth();

  const totals = useMemo(() => {
    // Default business settings
    const defaultSettings: BusinessSettings = {
      tax_rate: 8.375,
      late_fee_mode: 'flat',
      late_fee_amount: 25.00,
      currency: 'USD'
    };

    const settings = businessSettings || defaultSettings;

    // Calculate subtotal from line items
    const subtotal = items.reduce((sum, item) => {
      const amount = parseFloat(String(item.amount)) || 0;
      return sum + amount;
    }, 0);
    
    // Calculate tax based on business settings
    const taxRate = parseFloat(String(settings.tax_rate)) || 0;
    const customerTaxExempt = options?.customerTaxExempt === true;
    const taxableItems = customerTaxExempt ? [] : items.filter(item => item.is_taxable);
    const taxableAmount = taxableItems.reduce((sum, item) => {
      const amount = parseFloat(String(item.amount)) || 0;
      return sum + amount;
    }, 0);
    
    const taxAmount = taxableAmount * (taxRate / 100);
    
    // Calculate total
    const total = subtotal + taxAmount;
    
    // Calculate late fee if overdue
    let lateFee = 0;
    if (!options?.disableLateFees && dueDate && settings.late_fee_amount && settings.late_fee_mode !== 'off') { 
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to 00:00 local time
      const dueDateLocal = new Date(dueDate);
      dueDateLocal.setHours(0, 0, 0, 0);
      
      if (today > dueDateLocal) {
        if (settings.late_fee_mode === 'flat') {
          lateFee = parseFloat(String(settings.late_fee_amount)) || 0;
        } else if (settings.late_fee_mode === 'percent') {
          // Only apply percent late fee if balance is positive
          if (total > 0) {
            lateFee = total * (parseFloat(String(settings.late_fee_amount)) / 100);
          }
        }
      }
    }
    
    // Apply rounding rules (ensure subtotal + tax == total within 2 cents)
    const finalTotal = total + lateFee;
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
      lateFee: Math.round(lateFee * 100) / 100,
      finalTotal: Math.round(finalTotal * 100) / 100,
      currency: settings.currency
    };
  }, [items, dueDate, businessSettings, options?.customerTaxExempt, options?.disableLateFees]);

  const isOverdue = useMemo(() => {
    if (!dueDate || options?.disableLateFees) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateLocal = new Date(dueDate);
    dueDateLocal.setHours(0, 0, 0, 0);
    return today > dueDateLocal;
  }, [dueDate, options?.disableLateFees]);

  const validateCalculation = (totals: InvoiceTotals): boolean => {
    const calculatedTotal = totals.subtotal + totals.taxAmount;
    const difference = Math.abs(calculatedTotal - totals.total);
    return difference <= 0.02; // Within 2 cents
  };

  const formatCurrency = (amount: number, currency = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const isCalculationValid = validateCalculation(totals);

  return {
    totals,
    isCalculationValid,
    formatCurrency,
    validateCalculation,
    isOverdue
  };
}; 